import React, { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import Layout from "./components/Layout.jsx";
import { legacyRoutes } from "./data/legacyRoutes.js";
import { routeSectionSlugs } from "./data/navigation.js";

const Home = lazy(() => import("./pages/Home.jsx"));
const StartHere = lazy(() => import("./pages/StartHere.jsx"));
const Programs = lazy(() => import("./pages/Programs.jsx"));
const ProgramDetail = lazy(() => import("./pages/ProgramDetail.jsx"));
const SeasonDetail = lazy(() => import("./pages/SeasonDetail.jsx"));
const Media = lazy(() => import("./pages/Media.jsx"));
const MediaSeasonDetail = lazy(() => import("./pages/MediaSeasonDetail.jsx"));
const EpisodeDetail = lazy(() => import("./pages/EpisodeDetail.jsx"));
const Courses = lazy(() => import("./pages/Courses.jsx"));
const CourseDetail = lazy(() => import("./pages/CourseDetail.jsx"));
const Events = lazy(() => import("./pages/Events.jsx"));
const Articles = lazy(() => import("./pages/Articles.jsx"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail.jsx"));
const Library = lazy(() => import("./pages/Library.jsx"));
const Downloads = lazy(() => import("./pages/Downloads.jsx"));
const DownloadDetail = lazy(() => import("./pages/DownloadDetail.jsx"));
const About = lazy(() => import("./pages/About.jsx"));
const Donate = lazy(() => import("./pages/Donate.jsx"));
const LegacyRedirect = lazy(() => import("./pages/LegacyRedirect.jsx"));
const NotFound = lazy(() => import("./pages/NotFound.jsx"));
const PassthroughBoundary = lazy(() => import("./pages/PassthroughBoundary.jsx"));

function uniqueSlugs(sectionKey) {
  return [...new Set(Object.values(routeSectionSlugs[sectionKey]))];
}

function page(Component) {
  return (
    <Suspense fallback={<div className="tgwe-loading">Loading</div>}>
      <Component />
    </Suspense>
  );
}

function sectionRoutes(sectionKey, Component) {
  return uniqueSlugs(sectionKey).map((path) => ({ path, element: page(Component) }));
}

function detailRoutes(sectionKey, Component) {
  return uniqueSlugs(sectionKey).map((path) => ({ path: `${path}/:slug`, element: page(Component) }));
}

function seasonRoutes() {
  return uniqueSlugs("programs").map((path) => ({
    path: `${path}/:programSlug/:seasonSlug`,
    element: page(SeasonDetail)
  }));
}

function legacyRedirectRoutes() {
  return legacyRoutes.map((route) => ({
    path: route.legacyPath,
    element: page(LegacyRedirect)
  }));
}

function passthroughBoundaryRoutes() {
  return [
    "/university/*",
    "/articles/*",
    "/images/*",
    "/media/*",
    "/pods/*",
    "/videos/*",
    "/google4abdf61eaad93335.html",
    "/tgwe-rssfeed.xml",
    "/sitemap.xml"
  ].map((path) => ({
    path,
    element: page(PassthroughBoundary)
  }));
}

const routes = [
  {
    path: "/",
    element: <Navigate to="/en/" replace />
  },
  ...legacyRedirectRoutes(),
  ...passthroughBoundaryRoutes(),
  {
    path: "/:lang",
    element: <Layout />,
    children: [
      { index: true, element: page(Home) },
      ...sectionRoutes("start-here", StartHere),
      ...sectionRoutes("programs", Programs),
      ...seasonRoutes(),
      ...detailRoutes("programs", ProgramDetail),
      ...sectionRoutes("media", Media),
      ...detailRoutes("media", MediaSeasonDetail),
      ...detailRoutes("episodes", EpisodeDetail),
      ...sectionRoutes("courses", Courses),
      ...detailRoutes("courses", CourseDetail),
      ...sectionRoutes("events", Events),
      ...sectionRoutes("articles", Articles),
      ...detailRoutes("articles", ArticleDetail),
      ...sectionRoutes("library", Library),
      ...sectionRoutes("downloads", Downloads),
      ...detailRoutes("downloads", DownloadDetail),
      ...sectionRoutes("about", About),
      ...sectionRoutes("donate", Donate),
      { path: "*", element: page(NotFound) }
    ]
  },
  {
    path: "*",
    element: page(LegacyRedirect)
  }
];

const router = createBrowserRouter(routes);

export default router;
